import { call, put, takeEvery, fork, select } from 'redux-saga/effects';
import { get, isEmpty } from 'lodash';

import {
	MERCHANT_PROFILE_REQUESTING,
	MERCHANT_PROFILE_SUCCESS,
	MERCHANT_PROFILE_ERROR,
	MERCHANT_LIST_REQUESTING,
	MERCHANT_LIST_SUCCESS,
	MERCHANT_LIST_ERROR,
	UPDATE_USER_PROFILE_SUCCESS,
	UPDATE_USER_PROFILE_ERROR,
	UPDATE_USER_PROFILE_REQUESTING,
	SAVE_MERCHANT_ONBOARDING_INFO,
	SET_MERCHANT_FEATURE_CONFIG,
	REQUESTING_FEATURE_CONFIG,
} from '@/redux/ducks/merchantProfile/types';
import { SHOW_TOAST } from '@/redux/ducks/toast/types';
import { INIT_APP_ERROR } from '@/redux/ducks/init/types';
import api from '@/data';
import Constants from '@/utils/constants';
import { saveData, getData } from '@/data/cache';
import { typeHasPermission } from '@/utils/permissions';
import { SERVICE_LINE_LIST_REQUESTING } from '@/online-merchant-onboarding/redux/ducks/onboarding/type';
import {
	initializeGarfieldWithTrackerID,
	setAuthFlowGarfield,
} from '@/utils/GarfieldUtils';
import { SET_SELECTED_MERCHANTS } from '@/redux/ducks/userConfig/types';
import { morphMerchantList } from '@/components/shared/merchantList/MerchantListSelect';
import { Toast } from '@/widgets';
import TextConstant from '@/constants/textConstant';
import { sendCustomAnalyticsEvent } from '@/utils/AnalyticsUtils';
import { NUDGE_MERCHANT_STATE_EMPTY } from '@/constants';
import { store } from '@/redux/store/store';

import { getUserInfo } from './userApiSaga';

export const getUserId = (state) => state.userAuth.user.userId;
// Function to fetch dashboard startup config of a merchant
export function* fetchDashboardStartupConfig() {
	try {
		const dashboardStartupConfig = yield call(
			api.getDashboardStartupConfigDetails,
		);
		const merchantStateObject = get(
			dashboardStartupConfig,
			'data.response.merchantState',
			null,
		);

		if (isEmpty(merchantStateObject)) {
			sendCustomAnalyticsEvent('custom', {
				name: NUDGE_MERCHANT_STATE_EMPTY,
			});
		}
		yield put({
			type: SET_MERCHANT_FEATURE_CONFIG,
			data: get(dashboardStartupConfig, 'data.response', {}),
		});
	} catch (error) {
		Toast.error(
			get(error, 'data.message') || TextConstant.SOMETHING_WENT_WRONG,
		);
	}
}

// Function to make api calls and setup data specific for online merchants
function* onlineMerchantSetup(merchantProfileData) {
	try {
		if (typeHasPermission(Constants.PERMISSIONS.online_merchant_onboarding)) {
			const merchantOnboardingRes = yield call(
				api.getMerchantOnboardingDetails,
			);
			const onboardingInfo = get(merchantOnboardingRes, 'data.data', {});
			yield put({
				type: SAVE_MERCHANT_ONBOARDING_INFO,
				data: { ...onboardingInfo },
			});
			yield put({
				type: SERVICE_LINE_LIST_REQUESTING,
				data: merchantProfileData.merchantId,
			});
		}
		if (!merchantProfileData.blacklisted) {
			yield call(fetchDashboardStartupConfig);
		}
	} catch (error) {
		Toast.error(
			get(error, 'data.message') || TextConstant.SOMETHING_WENT_WRONG,
		);
	}
}

// Function  to get merchant profile and merchant hashIdfrom API
export function* getMerchantProfile() {
	try {
		const response = yield call(api.getMerchantProfile);
		let merchantHashId;
		const responseData = get(response, 'data.data', {});
		try {
			if (responseData.merchantId) {
				// API call to get hashId
				const merchantHashRes = yield call(
					api.getMerchantHashId,
					responseData.merchantId,
				);
				merchantHashId = merchantHashRes.data.data.hashedId;
			}
		} catch (e) {
			console.error(e);
		}

		if (responseData.type === Constants.ONLINE_MERCHANT) {
			yield call(onlineMerchantSetup, responseData);
		}

		yield put({
			type: MERCHANT_PROFILE_SUCCESS,
			data: { ...responseData, merchantHashId },
		});
		yield call(saveData, Constants.MERCHANT_PROFILE_KEY, {
			...responseData,
			merchantHashId,
		});
		const merchantType = yield select((state) =>
			get(state, 'merchantProfile.merchantDetails.type'),
		);
		if (merchantType === Constants.ONLINE_MERCHANT) {
			initializeGarfieldWithTrackerID(Constants.TRACKER_ID_ONLINE_DASHBOARD);
			setAuthFlowGarfield(true);
		}
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({ type: MERCHANT_PROFILE_ERROR, error: error.data });
		}
	}
}

export function* getMerchantProfileWatcher() {
	yield takeEvery(MERCHANT_PROFILE_REQUESTING, getMerchantProfile);
}

// Function to get merchant List and save in localstorage
export function* fetchMerchantList() {
	try {
		const response = yield call(api.merchantList);
		yield put({ type: MERCHANT_LIST_SUCCESS, data: response.data?.data });
		yield call(saveData, Constants.MERCHANT_LIST_KEY, response.data?.data);
		if (!isEmpty(response.data?.data)) {
			const userConfigStore = store?.getState()?.userConfig || {};
			for (const routeName of Constants.SUPERMID_SELECTION_ROUTES
				.multiSelection) {
				if (isEmpty(userConfigStore[routeName]?.selectedMerchants)) {
					yield put({
						type: SET_SELECTED_MERCHANTS,
						payload: {
							routeName,
							selectedMerchants: morphMerchantList(response.data?.data),
						},
					});
				}
			}
			for (const routeName of Constants.SUPERMID_SELECTION_ROUTES
				.singleSelection) {
				if (isEmpty(userConfigStore[routeName]?.selectedMerchants)) {
					yield put({
						type: SET_SELECTED_MERCHANTS,
						payload: {
							routeName,
							selectedMerchants: [morphMerchantList(response.data?.data)[0]],
						},
					});
				}
			}
		}
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({ type: MERCHANT_LIST_ERROR, error: error.data });
		}
	}
}

export function* userProfileUpdate(action) {
	try {
		const { payload } = action;
		const userInfo = yield call(getData, Constants.USER_INFO_KEY);
		payload.updatedBy = userInfo.userId;
		const response = yield call(api.changeUserData, userInfo.userId, payload);
		if (response.status === 204) {
			yield call(getUserInfo, userInfo.userId);
			let successMsg;
			if (payload.email) successMsg = 'Verification email sent successfully.';
			else successMsg = 'User Data Updated.';
			yield put({
				type: SHOW_TOAST,
				payload: { msg: successMsg, type: 'success' },
			});
			yield put({
				type: UPDATE_USER_PROFILE_SUCCESS,
				data: response.data,
			});
		}
	} catch (error) {
		yield put({
			type: UPDATE_USER_PROFILE_ERROR,
			error: error.data,
		});
		yield put({
			type: SHOW_TOAST,
			payload: { msg: get(error, 'data.message'), type: 'error' },
		});
	}
}

export function* getMerchantList() {
	yield takeEvery(MERCHANT_LIST_REQUESTING, fetchMerchantList);
}

export function* profileUpdate() {
	yield takeEvery(UPDATE_USER_PROFILE_REQUESTING, userProfileUpdate);
}

export function* fetchDashboardStartupConfigWatcher() {
	yield takeEvery(REQUESTING_FEATURE_CONFIG, fetchDashboardStartupConfig);
}

function* merchantFlowSaga() {
	yield fork(getMerchantProfileWatcher);
	yield fork(getMerchantList);
	yield fork(profileUpdate);
	yield fork(fetchDashboardStartupConfigWatcher);
}

export default merchantFlowSaga;
